import {Alert, Anchor, Badge, Group, Loader, Paper, Stack, Text, Title} from '@mantine/core'

import {useDeployEquipment} from 'src/equipment-deployment/use-deploy-equipment'
import {useDeployStockEquipment} from 'src/equipment-deployment/use-deploy-stock-equipment'

import {formatDateTbcLabel, formatJobScheduleRange} from '../jobs/job-schedule-format'
import {FoldedText} from '../ui/folded-text'
import {DriverEquipmentCard} from './driver-equipment-card'
import {DriverJobComments} from './driver-job-comments'
import {DriverJobDocumentsSection} from './driver-job-documents'
import {DriverJobLocations} from './driver-job-locations'
import {DriverLoadsCard} from './driver-loads-card'
import {DriverStockEquipmentCard} from './driver-stock-equipment-card'
import {DriverAccessError} from './errors'
import type {DriverJob} from './schemas'
import {useDriverJob} from './use-driver-job'

const driverSourceLabels: Record<string, string> = {
    job: 'Job POC',
    project: 'Order POC',
}

function DriverCustomerPocSection({job}: {job: DriverJob}) {
    if (!job.customerContact) {
        return null
    }

    const sourceLabel = job.customerContactSource
        ? driverSourceLabels[job.customerContactSource]
        : null

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="xs">
                <Group gap="sm" align="center">
                    <Title order={5}>Customer point of contact</Title>
                    {sourceLabel ? (
                        <Badge size="xs" variant="light" color="grape">
                            {sourceLabel}
                        </Badge>
                    ) : null}
                </Group>
                <Text size="sm" fw={500}>
                    {job.customerContact.name}
                </Text>
                {job.customerContact.title ? (
                    <Text size="sm" c="dimmed">
                        {job.customerContact.title}
                    </Text>
                ) : null}
                {job.customerContact.phone ? (
                    <Anchor href={`tel:${job.customerContact.phone}`} size="sm">
                        {job.customerContact.phone}
                    </Anchor>
                ) : null}
                {job.customerContact.email ? (
                    <Anchor href={`mailto:${job.customerContact.email}`} size="sm">
                        {job.customerContact.email}
                    </Anchor>
                ) : null}
            </Stack>
        </Paper>
    )
}

function DriverJobInstructions({job}: {job: DriverJob}) {
    const hasProjectInstructions = Boolean(job.projectDescription || job.projectNotes)
    const hasJobInstructions = Boolean(job.description || job.notes)

    return (
        <>
            {hasProjectInstructions ? (
                <Paper withBorder p="md" radius="md">
                    <Stack gap="xs">
                        <Title order={4}>Order instructions</Title>
                        {job.projectDescription ? (
                            <FoldedText
                                text={job.projectDescription}
                                label={
                                    <Text component="span" fw={600}>
                                        Description:{' '}
                                    </Text>
                                }
                            />
                        ) : null}
                        {job.projectNotes ? (
                            <FoldedText
                                text={job.projectNotes}
                                label={
                                    <Text component="span" fw={600}>
                                        Notes:{' '}
                                    </Text>
                                }
                            />
                        ) : null}
                    </Stack>
                </Paper>
            ) : null}

            {hasJobInstructions ? (
                <Paper withBorder p="md" radius="md">
                    <Stack gap="xs">
                        <Title order={4}>Job notes</Title>
                        {job.description ? (
                            <FoldedText
                                text={job.description}
                                label={
                                    <Text component="span" fw={600}>
                                        Description:{' '}
                                    </Text>
                                }
                            />
                        ) : null}
                        {job.notes ? (
                            <FoldedText
                                text={job.notes}
                                label={
                                    <Text component="span" fw={600}>
                                        Notes:{' '}
                                    </Text>
                                }
                            />
                        ) : null}
                    </Stack>
                </Paper>
            ) : null}
        </>
    )
}

type DriverJobPageProps = {
    jobId: string
    accessToken: string
}

export function DriverJobPage({jobId, accessToken}: DriverJobPageProps) {
    const {data: job, isLoading, isError, error} = useDriverJob(jobId, accessToken)
    const deployEquipment = useDeployEquipment(jobId, accessToken)
    const deployStockEquipment = useDeployStockEquipment(jobId, accessToken)

    if (!accessToken) {
        return (
            <Alert color="red" title="Link unavailable">
                This driver link is invalid or no longer available.
            </Alert>
        )
    }

    if (isLoading) {
        return (
            <Stack align="center" py="xl" gap="sm">
                <Loader size="sm" />
                <Text size="sm" c="dimmed">
                    Loading job…
                </Text>
            </Stack>
        )
    }

    if (isError || !job) {
        if (error instanceof DriverAccessError) {
            return (
                <Alert color="red" title="Link unavailable">
                    This driver link is invalid or no longer available.
                </Alert>
            )
        }

        const message = error instanceof Error ? error.message : 'Failed to load job'
        return (
            <Alert color="red" title="Unable to load job">
                {message}
            </Alert>
        )
    }

    const canRecord = job.driverAssignmentId != null
    const scheduleLabel = job.dateTbc
        ? formatDateTbcLabel(job.startTime)
        : formatJobScheduleRange({
              startDate: job.startDate,
              endDate: job.endDate === job.startDate ? null : job.endDate,
              startTime: job.startTime,
              endTime: null,
              style: 'compact',
          })

    return (
        <Stack gap="lg" pb="xl">
            <Stack gap={4}>
                <Text size="sm" c="dimmed" fw={600}>
                    Mat Tech
                </Text>
                <Title order={2}>
                    {job.displayId}: {job.name}
                </Title>
                <Text size="sm">{job.customerName}</Text>
                <Text size="sm" fw={500}>
                    {scheduleLabel}
                </Text>
            </Stack>

            {canRecord ? null : (
                <Alert color="yellow">
                    {job.driverName
                        ? 'You are no longer assigned to this job. Call dispatch.'
                        : 'Job link is view only. Ask dispatch for your driver link.'}
                </Alert>
            )}

            <DriverJobInstructions job={job} />

            <DriverCustomerPocSection job={job} />

            {job.stockEquipmentGroups.length > 0 || job.assetAssignments.length > 0 ? (
                <Stack gap="sm">
                    <Title order={4}>{job.isPickup ? 'Equipment to collect' : 'Equipment'}</Title>
                    {job.isPickup ? (
                        <Text size="sm" c="dimmed">
                            Bring this equipment back to the yard
                        </Text>
                    ) : null}
                    {job.stockEquipmentGroups.map((group) => (
                        <DriverStockEquipmentCard
                            key={`${group.rentalAssetTypeId}-${group.thirdPartyRentalAssetOfferingId ?? 'owned'}`}
                            group={group}
                            locations={job.locations}
                            isPickup={job.isPickup}
                            deploying={deployStockEquipment.isPending}
                            onDeliver={
                                canRecord
                                    ? async (input) => {
                                          const result = await deployStockEquipment.mutateAsync({
                                              rentalAssetTypeId: group.rentalAssetTypeId,
                                              thirdPartyRentalAssetOfferingId:
                                                  group.thirdPartyRentalAssetOfferingId ??
                                                  undefined,
                                              ...input,
                                          })
                                          return {
                                              assignedQuantity: result.assignedQuantity,
                                              deliveredQuantity: result.deliveredQuantity,
                                              remainingQuantity: result.remainingQuantity,
                                              deliveredByLocation: result.deliveredByLocation,
                                          }
                                      }
                                    : undefined
                            }
                        />
                    ))}
                    {job.assetAssignments.map((assignment) => (
                        <DriverEquipmentCard
                            key={assignment.id}
                            assignment={assignment}
                            locations={job.locations}
                            isPickup={job.isPickup}
                            deploying={deployEquipment.isPending}
                            onMarkDelivered={
                                canRecord
                                    ? async (jobLocationId) => {
                                          await deployEquipment.mutateAsync({
                                              rentalAssetId: assignment.rentalAssetId,
                                              jobLocationId,
                                          })
                                      }
                                    : undefined
                            }
                        />
                    ))}
                </Stack>
            ) : null}

            {job.loads ? (
                <DriverLoadsCard job={job} loads={job.loads} accessToken={accessToken} />
            ) : null}

            <DriverJobComments comments={job.comments} />

            {job.locations.length > 0 ? <DriverJobLocations locations={job.locations} /> : null}

            <DriverJobDocumentsSection job={job} accessToken={accessToken} />
        </Stack>
    )
}
